import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {BenjiKIVGameAssets} from "../../BenjiKIVGameAssets";
import {BenjiKIV} from "../../BenjiKIV";
import {BasicBitmapCountUp} from "../../components/BasicBitmapCountUp";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {gsap} from "gsap";
import {BenjiKIVSound} from "../../sound/BenjiKIVSound";
import {SpineUtils} from "../../utils/SpineUtils";
import {ParticleController} from "../../components/particles/ParticleController";
import {ParticleSettings} from "../../components/particles/ParticleSettings";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {Multiplier} from "../../components/Multiplier";

/**
 * Created by Jerker Nord on 2022-11-09.
 */

export class BenjiKIVBigWinView extends PIXI.Container implements StageLayer {

    private static SPINE_ANIMATION_NAMES:string[] = ["BW_1", "BW_2", "BW_3", "BW_4", "BW_5"];
    private static SPINE_ANIMATION_LEVEL_SPEED:number[] = [0.81, 0.81, 0.81, 0.81, 0.81];

    private _countUp:BasicBitmapCountUp;
    private _background:PIXI.Sprite;
    private _bigWinSpine:PIXI.spine.Spine;
    private _currentLevel:number;

    private _particleEffectsLoops: ParticleController[] = [];
    private _particleEffectsSplash: ParticleController[] = [];
    private _smoke: TimelineSprite;

    constructor() {
        super();
        this.name = "BenjiKIVBigWinView";
        this.init();
    }

    private init():void {
        this._background = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._background.tint = 0x000000;
        this._background.alpha = 0;
        this.addChild(this._background);

        this._bigWinSpine = new PIXI.spine.Spine(BenjiKIV.assetLoader.getSpineAsset(BenjiKIVGameAssets.BIG_WIN_SPINE));
        this._bigWinSpine.position.set(360, 360);
        this._bigWinSpine.visible = false;
        this.addChild(this._bigWinSpine);

        this._smoke = new TimelineSprite(BenjiKIV.assetLoader.getTextures("FX_BW_Fire/BW_Fire_"));
        this._smoke.hide();
        this._smoke.anchor.set(0.5);
        this._smoke.scale.set(2);
        this._smoke.position.set(360, 560);
        this._smoke.blendMode = PIXI.BLEND_MODES.ADD;
        this.addChild(this._smoke);

        // #154
        this.initFountains();

        this._countUp = new BasicBitmapCountUp({
            fontName : BenjiKIVGameAssets.BITMAP_FONT_BIG_WIN_COUNT_UP,
            fontSize : 86,
            tint : 0xffffff
        }, 720, new PIXI.Point(0.5, 0.5));
        this._countUp.scale.set(0.8, 0.8);
        this._countUp.stopAt(0);
        this._countUp.position.set(360, 640);
        this._countUp.alpha = 0;

        // #154
        BenjiKIV.bigWinCountUp.addChild(this._countUp);

        this.onResize();
    }

    private initFountains(): void {
        this._particleEffectsLoops.push(BenjiKIV.particleManager.createAnimation(this, "bulletLoop", "bullet", ParticleSettings.coinsConfigs, true));
        this._particleEffectsLoops.push(BenjiKIV.particleManager.createAnimation(this, "toothLoop", "tooth", ParticleSettings.coinsConfigs, true));

        this._particleEffectsSplash.push(BenjiKIV.particleManager.createAnimation(this, "bulletSplash", "bullet", ParticleSettings.coinsConfigsSplash, true));
        this._particleEffectsSplash.push(BenjiKIV.particleManager.createAnimation(this, "toothSplash", "tooth", ParticleSettings.coinsConfigsSplash, true));
    }

    private moveCountUpUpwards(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        tl.to(this._countUp, {
            duration: 0.3,
            y: 500
        });
        tl.add(() => {
            BenjiKIV.winWays.setYPosWays(200);
            BenjiKIV.winWays.showInitialWinWays();
        });

        return tl;
    }

    public getCountUpScaleAnimation(duration:number, ease:string, to:number, from?:number):gsap.core.Timeline {
        const tl = gsap.timeline();
        if(from != undefined) {
            tl.add(() => {
                this._countUp.scale.set(from, from)
            });
        }
        tl.add(gsap.to(this._countUp.scale, {duration : duration, x : to, y : to, ease : ease}))

        return tl;
    }

    public precalculateScale(endValue:number):void {
        this._countUp.stopAt(0);
        this._countUp.preCalculateScale(0, endValue);
        this._countUp.y = 640;
    }

    public show():gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        tl.add(() => {this._background.alpha = 0});
        tl.add([
            gsap.to(this._background, {duration : 1.250, alpha : 0.85}),
            gsap.to(this._bigWinSpine, {duration : 0.2, alpha : 1}),
        ]);

        return tl;
    }

    public showEnd(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        tl.add([
            this.moveCountUpUpwards(),
            () => {
                BenjiKIV.multiplierCountUp.onBigWinEnd();
                BenjiKIV.multiplierCountUp.summariseMultiplierCountUp();

                if (this._currentLevel >= 3 && BenjiKIV.apiPlugin.gameClientConfiguration.useCensoredGfxUS) {
                    this._bigWinSpine.state.setAnimation(0, BenjiKIVBigWinView.SPINE_ANIMATION_NAMES[this._currentLevel] + "_WV", false);
                } else {
                    this._bigWinSpine.state.setAnimation(0, BenjiKIVBigWinView.SPINE_ANIMATION_NAMES[this._currentLevel], false);
                }

                this.emitFountain(-1);
                this.emitSplash(-1);

                // #227
                BenjiKIV.soundPlayer.stopSound(BenjiKIVSound.MULTIPLIER_COUNT_UP_BIG);
            }
        ]);
        tl.add(gsap.timeline({delay: 2}));

        return tl;
    }

    public hide():gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline(),
            duration: number = 0.2;

        tl.add([
            gsap.to(this._background, {duration: duration, alpha: 0}),
            gsap.to(this._countUp, {duration: duration, alpha: 0}),
            gsap.to([this._bigWinSpine, this._smoke], {
                duration: duration,
                alpha: 0,
                onComplete: () => {
                    this._bigWinSpine.visible = false;

                    this._smoke.hide();
                    this._smoke.alpha = 1;
                }
            }),
            () => {
                BenjiKIV.winWays.hideInitialWinWays(duration);

                BenjiKIV.multiplierCountUp.hideAllMultipliers();
            }
        ]);

        return tl;
    }

    public getLevelAnimation(countFrom:number, countTo:number, duration:number, level:number):gsap.core.Timeline {
        this._currentLevel = level - 1;
        const tl:gsap.core.Timeline = gsap.timeline();
        tl.add([
            () => {
                this._bigWinSpine.visible = true;
                SpineUtils.resetSpineAnimation(this._bigWinSpine);
                if (level >= 3 && BenjiKIV.apiPlugin.gameClientConfiguration.useCensoredGfxUS) {
                    this._bigWinSpine.state.setAnimation(0, BenjiKIVBigWinView.SPINE_ANIMATION_NAMES[level - 1] + "_WV", true).timeScale = BenjiKIVBigWinView.SPINE_ANIMATION_LEVEL_SPEED[level - 1];
                } else {
                    this._bigWinSpine.state.setAnimation(0, BenjiKIVBigWinView.SPINE_ANIMATION_NAMES[level - 1], true).timeScale = BenjiKIVBigWinView.SPINE_ANIMATION_LEVEL_SPEED[level - 1];
                }
                this.emitFountain(level);
                this.emitSplash(level);

                if (level > 3) {
                    this._smoke.playLoop();
                    this._smoke.show();
                }

                // #227
                BenjiKIV.multiplierCountUp.multipliers.forEach((val: Multiplier) => {
                    val.onBigWinLevelChange(level);
                });
            },
            this._countUp.getCountAnimation(countFrom, countTo, duration, true),
            () => {
                gsap.to(this._countUp, {duration : 0.2, alpha : 1});
                if(level > 1) {
                    BenjiKIV.soundPlayer.playNonLoopSound(BenjiKIVSound.BIG_WIN_MUSICS[level - 1]);
                }
            }
        ]);

        const scale:gsap.core.Timeline = gsap.timeline();
        scale.add(this.getCountUpScaleAnimation(0.1, "power2.in", 1.2));
        scale.add(this.getCountUpScaleAnimation(0.2, "power2.out", 1.0));
        scale.add(this.getCountUpScaleAnimation(tl.duration() - 0.3, "power2.in", 0.8));
        tl.add(scale, 0);
        return tl;
    }

    public onResize():void {
        this._background.width = NolimitApplication.screenBounds.width;
        this._background.height = NolimitApplication.screenBounds.height;
        this._background.x = NolimitApplication.screenBounds.left;
        this._background.y = NolimitApplication.screenBounds.top;
    }

    private emitFountain(lvl: number): void {
        this._particleEffectsLoops.forEach((p: ParticleController) => {
            p.enableEmit(false);
        });

        if (lvl > 4) {
            this._particleEffectsLoops[0].enableEmit(true);
            this._particleEffectsLoops[1].enableEmit(true);
        } else if (lvl >= 3) {
            this._particleEffectsLoops[0].enableEmit(true);
        }
    }

    private emitSplash(lvl: number): void {
        this._particleEffectsSplash.forEach((p: ParticleController) => {
            p.enableEmit(false);
        });

        if (lvl > 4) {
            this._particleEffectsSplash[0].enableEmit(true);
            this._particleEffectsSplash[1].enableEmit(true);
        } else if (lvl >= 3) {
            this._particleEffectsSplash[0].enableEmit(true);
        }
    }
}